import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { Highlight } from "@/components/Highlight";
import type { Tenant } from "@/types/api";

const VISIBLE_LIMIT = 50;

interface Props {
  tenants: Tenant[];
  value: string; // selected tenant_id (UUID), or ""
  onChange: (id: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}

export function TenantCombobox({
  tenants,
  value,
  onChange,
  disabled = false,
  loading = false,
  placeholder,
}: Props): JSX.Element {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const selected = tenants.find((t) => t.id === value);

  const filtered = useMemo(() => {
    if (!query) return tenants.slice(0, VISIBLE_LIMIT);
    const q = query.toLowerCase();
    const matches: Tenant[] = [];
    for (const t of tenants) {
      if (
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q)
      ) {
        matches.push(t);
        if (matches.length >= VISIBLE_LIMIT) break;
      }
    }
    return matches;
  }, [tenants, query]);

  // Keep activeIndex in bounds when filtered shrinks/grows.
  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(0);
  }, [filtered, activeIndex]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function selectTenant(t: Tenant): void {
    onChange(t.id);
    setQuery("");
    setOpen(false);
  }

  function clearSelection(): void {
    onChange("");
    setQuery("");
    setOpen(true);
    inputRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const t = filtered[activeIndex];
      if (open && t) {
        e.preventDefault();
        selectTenant(t);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && selected && !query) {
      // Quick-clear: backspace on empty input clears the selection.
      e.preventDefault();
      clearSelection();
    }
  }

  // The input shows the search query while open (so the user can type), and
  // the formatted selection while closed (so it reads like a settled value).
  const displayValue = open
    ? query
    : selected
      ? `${selected.name} (${selected.slug})`
      : "";

  const placeholderText =
    placeholder ?? (loading ? "Loading tenants…" : "Search tenants…");

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered[activeIndex]
            ? `${listboxId}-${filtered[activeIndex].id}`
            : undefined
        }
        autoComplete="off"
        spellCheck={false}
        className="input pr-8"
        disabled={disabled || loading}
        value={displayValue}
        placeholder={selected && !open ? "" : placeholderText}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onKeyDown={onKeyDown}
      />
      {selected && !open && !disabled && !loading && (
        <button
          type="button"
          aria-label="Clear selection"
          onClick={clearSelection}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-100 text-sm leading-none px-1"
        >
          ×
        </button>
      )}
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-ink-800 ring-1 ring-ink-600 rounded-md shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-400">
              {tenants.length === 0 ? "No tenants on file." : "No matches."}
            </li>
          ) : (
            filtered.map((t, i) => (
              <li
                key={t.id}
                id={`${listboxId}-${t.id}`}
                role="option"
                aria-selected={value === t.id}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  i === activeIndex ? "bg-ink-700" : "hover:bg-ink-700/50"
                } ${value === t.id ? "text-ink-100 font-medium" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault(); // don't blur the input
                  selectTenant(t);
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <Highlight text={t.name} query={query} />
                <span className="ml-2 text-xs text-ink-400">
                  <Highlight text={t.slug} query={query} />
                </span>
              </li>
            ))
          )}
          {!query && tenants.length > VISIBLE_LIMIT && (
            <li className="px-3 py-1.5 text-xs text-ink-400 border-t border-ink-700">
              Showing first {VISIBLE_LIMIT} of {tenants.length}. Type to
              narrow.
            </li>
          )}
          {query && filtered.length === VISIBLE_LIMIT && (
            <li className="px-3 py-1.5 text-xs text-ink-400 border-t border-ink-700">
              Showing first {VISIBLE_LIMIT} matches. Refine your search to see
              more.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
